import { useParams } from "react-router-dom";
import { useMonthlyPnlByAllocation } from "../api/useMonthlyPnlByAllocation";
import { useMonthlyPnlByAttribution } from "../api/useMonthlyPnlByAttribution";
import { useMemo } from "react";
import { TimeSeriesChartItem } from "@iliotech/widgets";
import { useGlobalCurrency, useStructureGrouping } from "../context";
import { ClickableLegendItem } from "@iliotech/component-library";
const labelMapper = new Map();

labelMapper.set("CashAndEquivalents", "Cash Balances");
labelMapper.set("FixedIncome", "Fixed Income");
labelMapper.set("RealEstate", "Real Estate");
labelMapper.set("CryptoCurrencies", "Crypto-Currencies");

const PRICE_COLOR = "#A8E6AE";
const FX_COLOR = "#A8C4E6";
const INCOME_COLOR = "#286B67";
export const usePnlPageMonthly = () => {
  const { portfolioId = "" } = useParams();
  const pnlByAllocation = useMonthlyPnlByAllocation(portfolioId);
  const pnlByAttribution = useMonthlyPnlByAttribution(portfolioId);
  const { currencyFormatter } = useGlobalCurrency();
  const { grouping, selectedGroup } = useStructureGrouping();

  const {
    byAllocation,
    byAttribution,
    byAttributionLegend,
    byAllocationLoading,
    byAttributionLoading,
    byAllocationLegend,
  } = useMemo(() => {
    const byAllocation: TimeSeriesChartItem[] =
      pnlByAllocation?.data?.data?.data
        ?.filter(
          (item) => !selectedGroup || item.assetClass === selectedGroup?.code
        )
        .map((item) => ({
          category: item.assetClass,
          entries: item.data.map((entry) => ({
            date: new Date(entry.date),
            value: entry.value,
          })),
          color: grouping?.getColor?.(item.assetClass),
        })) || [];

    let byAllocationLegend: ClickableLegendItem[] = [
      {
        label: "Total",
        value: currencyFormatter(pnlByAllocation?.data?.data?.total || 0),
      },
      ...(pnlByAllocation?.data?.data?.data || []).map((item) => {
        return {
          label: labelMapper.get(item.assetClass) || item.assetClass,
          color: grouping?.getColor?.(item.assetClass),
          value: currencyFormatter(item.assetClassTotal),
        };
      }),
    ];

    const byAttribution: TimeSeriesChartItem[] = [
      {
        category: "Fx",
        entries:
          pnlByAttribution?.data?.data?.data?.map((entry) => ({
            date: new Date(entry.valuationDate),
            value: entry.fx,
          })) || [],
        color: FX_COLOR,
      },
      {
        category: "Income",
        entries:
          pnlByAttribution?.data?.data?.data?.map((entry) => ({
            date: new Date(entry.valuationDate),
            value: entry.income,
          })) || [],
        color: INCOME_COLOR,
      },
      {
        category: "Price",
        entries:
          pnlByAttribution?.data?.data?.data?.map((entry) => ({
            date: new Date(entry.valuationDate),
            value: entry.price,
          })) || [],
        color: PRICE_COLOR,
      },
    ];

    const byAttributionLegend: ClickableLegendItem[] = [
      {
        label: "Total",
        value: currencyFormatter(pnlByAttribution?.data?.data?.total || 0),
      },
      {
        label: "Price",
        color: PRICE_COLOR,
        value: currencyFormatter(pnlByAttribution?.data?.data?.priceTotal || 0),
      },
      {
        label: "Fx",
        color: FX_COLOR,
        value: currencyFormatter(pnlByAttribution?.data?.data?.fxTotal || 0),
      },
      {
        label: "Income",
        color: INCOME_COLOR,
        value: currencyFormatter(
          pnlByAttribution?.data?.data?.incomeTotal || 0
        ),
      },
    ];
    return {
      byAllocation,
      byAllocationLoading: pnlByAllocation?.isLoading,
      byAttribution,
      byAttributionLoading: pnlByAttribution?.isLoading,
      byAttributionLegend,
      byAllocationLegend,
    };
  }, [pnlByAllocation.data, pnlByAttribution.data, selectedGroup, grouping]);

  return {
    byAllocation,
    byAllocationLoading,
    byAttribution,
    byAttributionLoading,
    byAttributionLegend,
    byAllocationLegend,
  };
};
